import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Badge,
  IconButton,
  TextField,
  InputAdornment,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Fade,
  Slide,
  Popover,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon
} from '@mui/material';
import {
  Message as MessageIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import messageService from '../services/messageService';
import websocketService from '../services/websocketService';
import MessageConversation from './MessageConversation';
import encryption from '../utils/encryption';
import { computePresenceFromPresence, getPresenceBadgeColorHex, buildPresenceTooltipFromPresence } from '../utils/presence';

const MessageSidebar = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeChats, setActiveChats] = useState([]); // Active taskbar chats (max 3)
  const [minimizedChats, setMinimizedChats] = useState(() => {
    try {
      const saved = localStorage.getItem('messageSidebar_minimized');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  }); // Chats closed from taskbar
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [newMessageOpen, setNewMessageOpen] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchedUsers, setSearchedUsers] = useState([]);
  const [searchedTeams, setSearchedTeams] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [isExpanded, setIsExpanded] = useState(() => {
    const saved = localStorage.getItem('messageSidebar_expanded');
    return saved !== null ? JSON.parse(saved) : false; // Default to closed
  });
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [unreadByChat, setUnreadByChat] = useState({});
  const [flashingChats, setFlashingChats] = useState(new Set());
  // Fixed position for widget
  const SAFE_RIGHT_OFFSET = 96; // extra space to avoid overlap with other widgets
  const CHAT_POPOVER_WIDTH = 350; // should match Popover width below
  const CHAT_GAP = 16; // visual gap between popup and chat elements
  const position = { right: 20, bottom: 20 };
  const [searchingUsers, setSearchingUsers] = useState(false);
  const searchTimeoutRef = useRef(null);
  const sidebarRef = useRef(null);
  const flashTimeouts = useRef({});
  
  // Helper to derive decrypted preview text for last message
  const getPreviewText = (conversation) => {
    try {
      if (!conversation || !conversation.lastMessage) return 'Start a conversation';
      const lm = conversation.lastMessage;
      // Prefer content -> message; support non-text types with a label
      const raw = lm.content || lm.message || '';
      const type = lm.messageType || lm.type || 'text';

      // If there is no textual content (e.g., attachment), show a generic label
      if (!raw) {
        if (type && type !== 'text') return 'Attachment';
        return 'Start a conversation';
      }

      // Try to decrypt; fall back to raw on failure or if not encrypted
      let text = raw;
      try {
        const decrypted = encryption.decrypt(raw);
        if (decrypted) text = decrypted;
      } catch (e) {
        // ignore and keep raw
      }

      // Trim to 30 chars
      if (text.length > 30) return `${text.substring(0, 30)}...`;
      return text;
    } catch (e) {
      return 'Start a conversation';
    }
  };
  
  // Conversation context menu state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuConversation, setMenuConversation] = useState(null);

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);

  // Chat window sizing state
  const [chatSizes, setChatSizes] = useState(() => {
    try {
      const saved = localStorage.getItem('messageSidebar_chatSizes');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });
  
  const [resizing, setResizing] = useState(null); // { chatId, startX, startY, startWidth, startHeight }
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Save chat sizes to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('messageSidebar_chatSizes', JSON.stringify(chatSizes));
    } catch (e) {
      // no-op
    }
  }, [chatSizes]);

  // Menu handlers
  const closeConversationMenu = () => { setMenuAnchor(null); };

  // Menu handlers
  const handleConversationMenu = (event, conversation) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuConversation(conversation);
  };

  // Delete handlers
  const handleDeleteConversation = (conversation) => {
    if (conversation) setConversationToDelete(conversation);
    // Close any overlapping overlays before opening the confirmation dialog
    closeConversationMenu();
    setIsExpanded(false);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteConversation = async () => {
    if (!conversationToDelete) return;
    try {
      await messageService.deleteConversation(conversationToDelete._id);
      setConversations(prev => prev.filter(c => c._id !== conversationToDelete._id));
      setActiveChats(prev => prev.filter(c => c._id !== conversationToDelete._id));
      setMinimizedChats(prev => prev.filter(id => id !== conversationToDelete._id));
    } catch (err) {
      console.error('Error deleting conversation:', err);
    } finally {
      setDeleteConfirmOpen(false);
      setConversationToDelete(null);
    }
  };

  // Chat window resize handlers
  const getChatSize = (chatId) => {
    return chatSizes[chatId] || { width: 300, height: 400 };
  };

  const updateChatSize = (chatId, newSize) => {
    setChatSizes(prev => ({
      ...prev,
      [chatId]: {
        width: Math.max(250, Math.min(600, newSize.width)), // Min 250px, max 600px
        height: Math.max(300, Math.min(800, newSize.height)) // Min 300px, max 800px
      }
    }));
  };

  const handleResizeStart = (e, chatId) => {
    e.preventDefault();
    const currentSize = getChatSize(chatId);
    setResizing({
      chatId,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: currentSize.width,
      startHeight: currentSize.height
    });
  };

  const handleResizeMove = (e) => {
    if (!resizing) return;
    
    // Horizontal: we always resize from the left edge for this widget
    const deltaX = resizing.startX - e.clientX; // Inverted because we're resizing from the left edge

    // Vertical: depends on whether the handle is on top or bottom
    const resizingFromTop = resizing.resizeType === 'heightTop' || resizing.resizeType === 'bothTop';
    const deltaY = resizingFromTop ? (resizing.startY - e.clientY) : (e.clientY - resizing.startY);
    
    let newSize = { 
      width: resizing.startWidth, 
      height: resizing.startHeight 
    };
    
    if (resizing.resizeType === 'width' || resizing.resizeType === 'both' || resizing.resizeType === 'bothTop' || !resizing.resizeType) {
      newSize.width = resizing.startWidth + deltaX;
    }
    
    if (resizing.resizeType === 'height' || resizing.resizeType === 'heightTop' || resizing.resizeType === 'both' || resizing.resizeType === 'bothTop') {
      newSize.height = resizing.startHeight + deltaY;
    }
    
    updateChatSize(resizing.chatId, newSize);
  };

  const handleResizeEnd = () => {
    setResizing(null);
  };

  // Global mouse events for resizing
  useEffect(() => {
    if (resizing) {
      const handleMouseMove = (e) => handleResizeMove(e);
      const handleMouseUp = () => handleResizeEnd();
      
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [resizing]);
  
  // Responsive: treat >= 1025px as desktop; hide taskbar below this
  const isDesktopLike = useMediaQuery('(min-width:1025px)');

  // Presence tracking for chat widget
  const [presenceByUserId, setPresenceByUserId] = useState({});
  const statusToColor = (status) => {
    switch (status) {
      case 'available': return '#22c55e';
      case 'away': return '#f59e0b';
      case 'lunch': return '#0ea5e9';
      case 'busy': return '#ef4444';
      case 'custom': return '#0ea5e9';
      default: return '#9ca3af';
    }
  };
  const getPresenceColor = (presence) => {
    if (!presence || !presence.isOnline) return '#9ca3af';
    return statusToColor(presence.status);
  };

  useEffect(() => {
    let mounted = true;
    const handlePresence = (payload) => {
      if (!mounted || !payload || !payload.userId) return;
      setPresenceByUserId(prev => {
        const existing = prev[payload.userId] || {};
        const updated = { ...existing, ...(payload.presence || {}) };
        return { ...prev, [payload.userId]: updated };
      });
    };
    const handleUserOnlineStatus = (payload) => {
      if (!mounted || !payload || !payload.userId) return;
      setPresenceByUserId(prev => ({
        ...prev,
        [payload.userId]: {
          ...(prev[payload.userId] || {}),
          isOnline: payload.isOnline,
          status: payload.status || (prev[payload.userId] && prev[payload.userId].status),
          statusMessage: payload.statusMessage || (prev[payload.userId] && prev[payload.userId].statusMessage),
          lastSeenAt: payload.lastSeenAt || (prev[payload.userId] && prev[payload.userId].lastSeenAt)
        }
      }));
    };

    websocketService.connect().then(() => {
      if (!mounted) return;
      websocketService.addEventListener('presence', handlePresence);
      websocketService.addEventListener('userOnlineStatus', handleUserOnlineStatus);
    }).catch(() => {});

    return () => {
      mounted = false;
      websocketService.removeEventListener('presence', handlePresence);
      websocketService.removeEventListener('userOnlineStatus', handleUserOnlineStatus);
    };
  }, []);

  // Save expanded state to localStorage
  useEffect(() => {
    localStorage.setItem('messageSidebar_expanded', JSON.stringify(isExpanded));
  }, [isExpanded]);

  // Persist minimized chats so closing with X is remembered
  useEffect(() => {
    try {
      localStorage.setItem('messageSidebar_minimized', JSON.stringify(minimizedChats));
    } catch (e) {
      // no-op
    }
  }, [minimizedChats]);

  // Notify layout about active chat tabs for padding purposes (respect responsive visibility)
  useEffect(() => {
    try {
      const hasTabsVisible = isDesktopLike && activeChats.length > 0;
      const evt = new CustomEvent('chat:activeTabs', { detail: { hasTabs: hasTabsVisible, height: hasTabsVisible ? 48 : 0 } });
      window.dispatchEvent(evt);
    } catch (e) {
      // no-op
    }
  }, [activeChats.length, isDesktopLike]);

  // Helper to open a chat and ensure it is visible (unminimized). On mobile/tablet the taskbar is hidden,
  // so we must force the conversation window to open rather than stay minimized.
  const openChatInTaskbar = (conversation) => {
    setActiveChats(prev => {
      const exists = prev.find(c => c._id === conversation._id);

      // Always remove from minimized list for this conversation
      setMinimizedChats(prevMin => prevMin.filter(id => id !== conversation._id));

      if (exists) {
        // Unminimize if it already exists in active chats
        return prev.map(c => 
          c._id === conversation._id 
            ? { ...c, isMinimized: false }
            : c
        );
      }

      // Add to active chats with isMinimized=false so it opens immediately
      const newChat = { ...conversation, isMinimized: false };
      const newActiveChats = [newChat, ...prev.filter(c => c._id !== conversation._id)].slice(0, 3);
      return newActiveChats;
    });
    setIsExpanded(false); // Collapse widget when opening chat
  };

  // Helper to close a chat from taskbar (moves back to widget)
  const closeChatFromTaskbar = (conversationId) => {
    setActiveChats(prev => prev.filter(c => c._id !== conversationId));
    setMinimizedChats(prev => Array.from(new Set([...prev, conversationId])));
  };

  // Helper to minimize/maximize a chat in taskbar
  const toggleChatMinimize = (conversationId) => {
    setActiveChats(prev => prev.map(chat => 
      chat._id === conversationId 
        ? { ...chat, isMinimized: !chat.isMinimized }
        : chat
    ));
  };

  // Position is now fixed, no need to save

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
    
    // Ensure WebSocket connection and then subscribe to messaging events
    websocketService.connect().then(() => {
      console.log('MessageSidebar: WebSocket connected, subscribing to events');
      websocketService.addEventListener('new_message', handleNewMessage);
      websocketService.addEventListener('messages_read', handleMessagesRead);
      websocketService.addEventListener('message_deleted', handleMessageDeleted);
    }).catch(err => {
      console.warn('MessageSidebar: WebSocket connect failed:', err);
    });
    
    return () => {
      websocketService.removeEventListener('new_message', handleNewMessage);
      websocketService.removeEventListener('messages_read', handleMessagesRead);
      websocketService.removeEventListener('message_deleted', handleMessageDeleted);
    };
  }, []);

  // Allow external pages (e.g., TeamHub) to open a conversation in the sidebar
  useEffect(() => {
    const handler = async (evt) => {
      try {
        const detail = (evt && evt.detail) ? evt.detail : {};
        let conv = detail.conversation || null;
        const conversationId = detail.conversationId;
        const teamId = detail.teamId;

        // If a conversationId was provided but not the full conversation, fetch it
        if (!conv && conversationId) {
          // Try to find from current list first
          conv = conversations.find(c => c._id === conversationId) || null;
          if (!conv) {
            const res = await messageService.getConversation(conversationId);
            if (res && res.success) conv = res.conversation;
          }
        }

        // If a teamId was provided (either via this event or future compatibility),
        // start or get the team conversation first.
        if (!conv && teamId) {
          const res = await messageService.getOrCreateTeamConversation(teamId);
          if (res && res.success) conv = res.conversation;
        }

        if (conv) {
          // Ensure it's in our local list for quick access
          setConversations(prev => {
            const exists = prev.some(c => c._id === conv._id);
            return exists ? prev : [conv, ...prev];
          });
          // Open in taskbar and expand the widget
          openChatInTaskbar(conv);
          setIsExpanded(true);
        }
      } catch (e) {
        console.error('Failed to open conversation from event', e);
      }
    };

    window.addEventListener('chat:openConversation', handler);
    // Also support a dedicated team-open event for convenience
    window.addEventListener('chat:openTeamConversation', handler);
    return () => {
      window.removeEventListener('chat:openConversation', handler);
      window.removeEventListener('chat:openTeamConversation', handler);
    };
  }, [conversations]);

  // Load conversations
  const loadConversations = async () => {
    try {
      setLoading(true);
      const response = await messageService.getConversations();
      if (response.success) {
        setConversations(response.conversations);
        // Calculate total unread
        const total = response.conversations.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);
        setUnreadTotal(total);
        
        // Initialize unread counts by chat
        const unreadCounts = {};
        response.conversations.forEach(conv => {
          if (conv.unreadCount > 0) {
            unreadCounts[conv._id] = conv.unreadCount;
          }
        });
        setUnreadByChat(unreadCounts);
        
        // Auto-populate active chats with 3 most recent conversations that aren't minimized
        const recentConvs = response.conversations
          .filter(c => !minimizedChats.includes(c._id))
          .slice(0, 3);
        
        setActiveChats(prevActive => {
          // Only update if we don't have any active chats yet
          if (prevActive.length === 0) {
            return recentConvs.map(c => ({ ...c, isMinimized: true })); // Start minimized
          }
          return prevActive;
        });
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle new message from WebSocket
  const handleNewMessage = (data) => {
    const message = data.data;
    const conversationId = message.conversation._id;
    
    // Update conversation list
    setConversations(prev => {
      const updated = [...prev];
      const index = updated.findIndex(c => c._id === conversationId);
      
      if (index >= 0) {
        // Update existing conversation
        updated[index] = {
          ...updated[index],
          lastMessage: message,
          lastMessageAt: message.createdAt,
          unreadCount: (updated[index].unreadCount || 0) + 1
        };
        // Move to top
        const [conv] = updated.splice(index, 1);
        updated.unshift(conv);
      } else {
        // Add new conversation
        updated.unshift({
          ...message.conversation,
          lastMessage: message,
          lastMessageAt: message.createdAt,
          unreadCount: 1
        });
      }
      
      return updated;
    });
    
    // Update unread counts
    setUnreadByChat(prev => ({
      ...prev,
      [conversationId]: (prev[conversationId] || 0) + 1
    }));
    setUnreadTotal(prev => prev + 1);
    
    // Flash the taskbar chat if it's active
    const activeChat = activeChats.find(c => c._id === conversationId);
    if (activeChat && (!activeChat.isOpen || activeChat.isMinimized)) {
      // Start flashing
      setFlashingChats(prev => new Set([...prev, conversationId]));
      
      // Clear previous timeout if exists
      if (flashTimeouts.current[conversationId]) {
        clearTimeout(flashTimeouts.current[conversationId]);
      }
      
      // Stop flashing after 5 seconds
      flashTimeouts.current[conversationId] = setTimeout(() => {
        setFlashingChats(prev => {
          const next = new Set(prev);
          next.delete(conversationId);
          return next;
        });
      }, 5000);
    }
    
    // Auto-add to taskbar if it's one of the 3 most recent with messages
    if (!minimizedChats.includes(conversationId)) {
      setConversations(prevConvs => {
        const allConvs = prevConvs.filter(c => !minimizedChats.includes(c._id));
        const topThree = allConvs.slice(0, 3);
        if (topThree.some(c => c._id === conversationId)) {
          const conv = allConvs.find(c => c._id === conversationId) || message.conversation;
          // Use timeout to ensure state updates are processed
          setTimeout(() => openChatInTaskbar(conv), 0);
        }
        return prevConvs;
      });
    }
  };

  // Handle messages read event
  const handleMessagesRead = (data) => {
    const { conversationId, userId } = data.data;
    
    if (userId === user._id) {
      // Current user read messages
      setConversations(prev => 
        prev.map(conv => 
          conv._id === conversationId 
            ? { ...conv, unreadCount: 0 }
            : conv
        )
      );
      
      // Clear unread for this chat
      setUnreadByChat(prev => {
        const next = { ...prev };
        delete next[conversationId];
        return next;
      });
      
      // Stop flashing if it was
      setFlashingChats(prev => {
        const next = new Set(prev);
        next.delete(conversationId);
        return next;
      });
      
      // Recalculate total
      setConversations(prev => {
        const total = prev.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);
        setUnreadTotal(total);
        return prev;
      });
    }
  };

  // Handle message deleted event
  const handleMessageDeleted = (data) => {
    // Refresh conversations to update last message if needed
    loadConversations();
  };

  // Search users and teams for new conversation
  const searchUsersAndTeams = async (query) => {
    if (!query || query.length < 2) {
      setSearchedUsers([]);
      setSearchedTeams([]);
      return;
    }

    try {
      setSearchingUsers(true);
      const [usersRes, teamsRes] = await Promise.all([
        messageService.searchUsers(query),
        messageService.searchTeams(query)
      ]);

      // Users handling (support various response shapes)
      let users = [];
      if (usersRes) {
        if (Array.isArray(usersRes)) users = usersRes;
        else if (Array.isArray(usersRes.users)) users = usersRes.users;
        else if (Array.isArray(usersRes.results)) users = usersRes.results;
        else if (usersRes.success && Array.isArray(usersRes.users)) users = usersRes.users;
      }
      setSearchedUsers(users);

      // Teams handling (support various response shapes)
      let teams = [];
      if (teamsRes) {
        if (Array.isArray(teamsRes)) teams = teamsRes;
        else if (Array.isArray(teamsRes.teams)) teams = teamsRes.teams;
        else if (Array.isArray(teamsRes.results)) teams = teamsRes.results;
        else if (teamsRes.success && Array.isArray(teamsRes.teams)) teams = teamsRes.teams;
      }
      setSearchedTeams(teams);
    } catch (error) {
      console.error('Error searching users/teams:', error);
      setSearchedUsers([]);
      setSearchedTeams([]);
    } finally {
      setSearchingUsers(false);
    }
  };

  // Handle user search with debounce
  const handleUserSearch = (query) => {
    setUserSearchQuery(query);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      searchUsersAndTeams(query);
    }, 300);
  };

  // Start new conversation (users or team)
  const startNewConversation = async () => {
    if (!selectedTeam && selectedUsers.length === 0) return;
    
    try {
      let response;
      if (selectedTeam) {
        response = await messageService.getOrCreateTeamConversation(selectedTeam._id);
      } else {
        response = await messageService.getOrCreateConversation(
          selectedUsers.map(u => u._id),
          false
        );
      }
      
      if (response && response.success) {
        openChatInTaskbar(response.conversation);
        setNewMessageOpen(false);
        setSelectedUsers([]);
        setSelectedTeam(null);
        setUserSearchQuery('');
        setSearchedUsers([]);
        setSearchedTeams([]);
        
        // Add to conversations if new
        if (response.isNew) {
          setConversations(prev => [response.conversation, ...prev]);
        }
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
    }
  };

  // Filter conversations based on search
  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    
    const displayName = messageService.getConversationDisplayName(conv, user._id);
    return (displayName || '').toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Toggle user selection
  const toggleUserSelection = (user) => {
    // If selecting a user, clear any selected team
    setSelectedTeam(null);
    setSelectedUsers(prev => {
      const exists = prev.find(u => u._id === user._id);
      if (exists) {
        return prev.filter(u => u._id !== user._id);
      }
      return [...prev, user];
    });
  };

  // Toggle team selection (single-select)
  const toggleTeamSelection = (team) => {
    // Selecting a team clears user selections
    setSelectedUsers([]);
    setSelectedTeam(prev => (prev && prev._id === team._id ? null : team));
  };

  // No drag functionality needed

  return (
    <>
      {/* Sidebar Container */}
      <Paper
        ref={sidebarRef}
        role="button"
        aria-label={isExpanded ? 'Close chat' : 'Open chat'}
        aria-expanded={isExpanded}
        sx={{
          position: 'fixed',
          right: position.right,
          bottom: position.bottom,
          width: 60,
          height: 60,
          maxHeight: 60,
          display: 'flex',
          flexDirection: 'column',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '50%', // Always a round launcher; content opens in Popover above
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          zIndex: 1400,
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          '&:hover': {
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
            transform: 'translateY(-2px)'
          }
        }}
        onClick={() => setIsExpanded(prev => !prev)} // Toggle popover on icon click
      >
        {/* Header */}
        <Box
          sx={{
            p: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: 'none',
            background: isExpanded ? 'linear-gradient(135deg, #60a5fa, #3b82f6)' : 'transparent',
            borderRadius: '50%',
            position: 'relative',
            width: '100%',
            height: '100%',
            boxSizing: 'border-box'
          }}
        >
          <Badge 
            badgeContent={unreadTotal} 
            color="error"
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {isExpanded ? (
              <CloseIcon 
                sx={{ 
                  color: '#ffffff', 
                  fontSize: '1.5rem'
                }} 
              />
            ) : (
              <MessageIcon 
                sx={{ 
                  color: '#60a5fa', 
                  fontSize: '1.5rem',
                  '&:hover': {
                    color: '#3b82f6'
                  }
                }} 
              />
            )}
          </Badge>
        </Box>

        {/* Content disabled in inline launcher; content now shown via Popover */}
        <Collapse in={false} timeout="auto" sx={{ display: 'none' }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Search Bar */}
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: '8px',
                    backgroundColor: 'rgba(0, 0, 0, 0.05)'
                  }
                }}
              />
            </Box>

            <Divider />

            {/* Conversations List */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : filteredConversations.length > 0 ? (
                <List sx={{ p: 0 }}>
                  {filteredConversations.map((conversation) => {
                    const displayName = messageService.getConversationDisplayName(conversation, user._id);
                    const avatar = messageService.getConversationAvatar(conversation, user._id);
                    const isSelected = activeChats.some(c => c._id === conversation._id && !c.isMinimized);
                    
                    return (
                      <ListItem
                        key={conversation._id}
                        button
                        selected={isSelected}
                        onClick={() => openChatInTaskbar(conversation)}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(37, 99, 235, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(37, 99, 235, 0.15)',
                            '&:hover': {
                              backgroundColor: 'rgba(37, 99, 235, 0.2)'
                            }
                          }
                        }}
                      >
                        <ListItemAvatar>
                          <Badge
                            badgeContent={conversation.unreadCount}
                            color="error"
                            invisible={!conversation.unreadCount}
                          >
                            <Avatar
                              src={avatar ? `/api/auth/avatar-proxy?url=${encodeURIComponent(avatar)}` : undefined}
                              sx={{
                                backgroundColor: conversation.isGroup ? '#1e40af' : '#2563eb'
                              }}
                            >
                              {conversation.isGroup ? <GroupIcon /> : ((displayName || '').charAt(0).toUpperCase() || '?')}
                            </Avatar>
                          </Badge>
                        </ListItemAvatar>
                        <ListItemText
                          primary={displayName}
                          secondary={getPreviewText(conversation)}
                          primaryTypographyProps={{
                            fontWeight: conversation.unreadCount ? 600 : 400,
                            fontSize: '0.95rem'
                          }}
                          secondaryTypographyProps={{
                            fontSize: '0.85rem',
                            sx: {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }
                          }}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', position: 'relative' }}>
                          <Typography variant="caption" color="text.secondary">
                            {conversation.lastMessageAt && 
                              messageService.formatMessageTime(conversation.lastMessageAt)
                            }
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => handleConversationMenu(e, conversation)}
                            sx={{ 
                              position: 'absolute',
                              top: 0,
                              right: -8,
                              opacity: 0,
                              transition: 'opacity 0.2s ease',
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 1)'
                              },
                              '.MuiListItem-root:hover &': { 
                                opacity: 1 
                              }
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No conversations yet
                  </Typography>
                </Box>
              )}
            </Box>

            {/* New Message Button */}
            <Box sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setNewMessageOpen(true)}
                sx={{
                  background: 'linear-gradient(135deg, #2563eb, #1e40af)',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                New Message
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Paper>

      {/* Launcher Popover for conversations */}
      <Popover
        open={isExpanded}
        anchorEl={sidebarRef.current}
        onClose={() => setIsExpanded(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ zIndex: 1800 }}
      >
        <Paper sx={{ width: 350, maxHeight: '70vh', display: 'flex', flexDirection: 'column', borderRadius: '12px' }}>
          {/* Search Bar */}
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: '8px',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)'
                }
              }}
            />
          </Box>

          <Divider />

          {/* Conversations List */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : filteredConversations.length > 0 ? (
              <List sx={{ p: 0 }}>
                {filteredConversations.map((conversation) => {
                  const displayName = messageService.getConversationDisplayName(conversation, user._id);
                  const avatar = messageService.getConversationAvatar(conversation, user._id);
                  const isSelected = activeChats.some(c => c._id === conversation._id && !c.isMinimized);
                  
                  return (
                    <ListItem
                      key={conversation._id}
                      button
                      selected={isSelected}
                      onClick={() => openChatInTaskbar(conversation)}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(37, 99, 235, 0.1)'
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(37, 99, 235, 0.15)',
                          '&:hover': {
                            backgroundColor: 'rgba(37, 99, 235, 0.2)'
                          }
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Badge
                          badgeContent={conversation.unreadCount}
                          color="error"
                          invisible={!conversation.unreadCount}
                        >
                          {(() => {
                            const participants = (conversation.participants || []);
                            const others = participants.filter(p => p._id !== user._id);

                            let presenceObj = null;
                            if (conversation.isGroup) {
                              // Use Staff Directory logic: show active if any participant was active recently
                              const presences = others
                                .map(p => presenceByUserId[p._id])
                                .filter(Boolean);
                              const activePresences = presences.filter(p => computePresenceFromPresence(p).isActive);
                              if (activePresences.length > 0) {
                                // Pick the most recently active for tooltip purposes
                                activePresences.sort((a, b) => {
                                  const ad = new Date(a.lastActiveAt || a.lastSeenAt || 0).getTime();
                                  const bd = new Date(b.lastActiveAt || b.lastSeenAt || 0).getTime();
                                  return bd - ad;
                                });
                                presenceObj = activePresences[0];
                              } else {
                                presenceObj = null; // treated as offline
                              }
                            } else {
                              const other = others[0];
                              presenceObj = other ? (presenceByUserId[other._id] || null) : null;
                            }

                            const presenceColor = getPresenceBadgeColorHex(presenceObj);
                            const presenceText = buildPresenceTooltipFromPresence(presenceObj);

                            return (
                              <Box sx={{ position: 'relative' }}>
                                <Avatar
                                  src={avatar ? `/api/auth/avatar-proxy?url=${encodeURIComponent(avatar)}` : undefined}
                                  sx={{
                                    backgroundColor: conversation.isGroup ? '#1e40af' : '#2563eb'
                                  }}
                                >
                                  {conversation.isGroup ? <GroupIcon /> : ((displayName || '').charAt(0).toUpperCase() || '?')}
                                </Avatar>
                                <Box
                                  title={presenceText}
                                  sx={{
                                    position: 'absolute',
                                    right: -2,
                                    bottom: -2,
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    border: '2px solid #ffffff',
                                    backgroundColor: presenceColor
                                  }}
                                />
                              </Box>
                            );
                          })()}
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={displayName}
                        secondary={getPreviewText(conversation)}
                        primaryTypographyProps={{
                          fontWeight: conversation.unreadCount ? 600 : 400,
                          fontSize: '0.95rem'
                        }}
                        secondaryTypographyProps={{
                          fontSize: '0.85rem',
                          sx: {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }
                        }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', position: 'relative' }}>
                        <Typography variant="caption" color="text.secondary">
                          {conversation.lastMessageAt && 
                            messageService.formatMessageTime(conversation.lastMessageAt)
                          }
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => handleConversationMenu(e, conversation)}
                          sx={{ 
                            position: 'absolute',
                            top: 0,
                            right: -8,
                            opacity: 0,
                            transition: 'opacity 0.2s ease',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 1)'
                            },
                            '.MuiListItem-root:hover &': { 
                              opacity: 1 
                            }
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No conversations yet
                </Typography>
              </Box>
            )}
          </Box>

          {/* New Message Button */}
          <Box sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => {
                setIsExpanded(false);
                setNewMessageOpen(true);
              }}
              sx={{
                background: 'linear-gradient(135deg, #2563eb, #1e40af)',
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              New Message
            </Button>
          </Box>
        </Paper>
      </Popover>

      {/* Taskbar for active chats */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          right: `${position.right + SAFE_RIGHT_OFFSET + 60 + 12}px`,
          left: 'auto',
          height: '48px',
          backgroundColor: 'rgba(30, 58, 138, 0.95)',
          backdropFilter: 'blur(10px)',
          display: isDesktopLike && activeChats.length > 0 ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1,
          px: 2,
          zIndex: 1500,
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.2)',
          maxWidth: '50vw',
          overflowX: 'auto',
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px'
        }}
      >
        {activeChats.map((chat) => {
          const displayName = messageService.getConversationDisplayName(chat, user._id);
          const avatar = messageService.getConversationAvatar(chat, user._id);
          const isFlashing = flashingChats.has(chat._id);
          const unreadCount = unreadByChat[chat._id] || 0;
          
          return (
            <Paper
              key={chat._id}
              onClick={() => toggleChatMinimize(chat._id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.5,
                minWidth: '200px',
                maxWidth: '250px',
                cursor: 'pointer',
                backgroundColor: chat.isMinimized ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                borderRadius: '8px 8px 0 0',
                position: 'relative',
                transition: 'all 0.3s ease',
                animation: isFlashing ? 'flash 0.5s ease-in-out infinite' : 'none',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                  transform: 'translateY(-2px)'
                },
                '@keyframes flash': {
                  '0%, 100%': { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
                  '50%': { backgroundColor: 'rgba(255, 182, 193, 0.9)' }
                }
              }}
            >
              {(() => {
                const participants = (chat.participants || []);
                const others = participants.filter(p => p._id !== user._id);

                let presenceObj = null;
                if (chat.isGroup) {
                  const presences = others
                    .map(p => presenceByUserId[p._id])
                    .filter(Boolean);
                  const activePresences = presences.filter(p => computePresenceFromPresence(p).isActive);
                  if (activePresences.length > 0) {
                    activePresences.sort((a, b) => {
                      const ad = new Date(a.lastActiveAt || a.lastSeenAt || 0).getTime();
                      const bd = new Date(b.lastActiveAt || b.lastSeenAt || 0).getTime();
                      return bd - ad;
                    });
                    presenceObj = activePresences[0];
                  } else {
                    presenceObj = null;
                  }
                } else {
                  const other = others[0];
                  presenceObj = other ? (presenceByUserId[other._id] || null) : null;
                }

                const presenceColor = getPresenceBadgeColorHex(presenceObj);
                const presenceText = buildPresenceTooltipFromPresence(presenceObj);

                return (
                  <Box sx={{ position: 'relative' }}>
                    <Avatar
                      src={avatar ? `/api/auth/avatar-proxy?url=${encodeURIComponent(avatar)}` : undefined}
                      sx={{ width: 28, height: 28 }}
                    >
                      {((displayName || '').charAt(0).toUpperCase() || '?')}
                    </Avatar>
                    <Box
                      title={presenceText}
                      sx={{
                        position: 'absolute',
                        right: -2,
                        bottom: -2,
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        border: '2px solid #ffffff',
                        backgroundColor: presenceColor
                      }}
                    />
                  </Box>
                );
              })()}
              
              <Typography 
                variant="body2" 
                sx={{ 
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: unreadCount > 0 ? 600 : 400
                }}
              >
                {displayName}
              </Typography>
              
              {unreadCount > 0 && (
                <Badge 
                  badgeContent={unreadCount} 
                  color="error"
                  sx={{ mr: 1 }}
                />
              )}
              
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  closeChatFromTaskbar(chat._id);
                }}
                sx={{ 
                  p: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.1)'
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Paper>
          );
        })}
      </Box>

      {/* Chat Windows - Pop up from bottom */}
      {activeChats.map((chat) => {
        const index = activeChats.findIndex(c => c._id === chat._id);
        const reversedIndex = activeChats.length - 1 - index; // Ensure popups align with right-to-left taskbar order
        const chatSize = getChatSize(chat._id);
        const offset = reversedIndex * (chatSize.width + 20); // Dynamic offset based on chat width
        
        return (
          <Slide
            key={chat._id}
            direction="up"
            in={!chat.isMinimized}
            mountOnEnter
            unmountOnExit
          >
            <Paper
              sx={{
                position: 'fixed',
                bottom: isDesktopLike ? '48px' : `${position.bottom + 60 + 8}px`,
                right: `${(
                  isDesktopLike
                    ? position.right + SAFE_RIGHT_OFFSET
                    : position.right
                ) + offset}px`,
                width: `${chatSize.width}px`,
                height: `${chatSize.height}px`,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px 12px 0 0',
                boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
                zIndex: 1600,
                overflow: 'hidden',
                border: resizing?.chatId === chat._id ? '2px solid #2563eb' : 'none',
                transition: resizing?.chatId === chat._id ? 'none' : 'all 0.2s ease',
                pointerEvents: deleteConfirmOpen ? 'none' : 'auto'
              }}
            >
              {/* Resize Handle - Left Edge */}
              <Box
                onMouseDown={(e) => handleResizeStart(e, chat._id)}
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '8px',
                  cursor: 'ew-resize',
                  backgroundColor: 'transparent',
                  zIndex: 10,
                  '&:hover': {
                    backgroundColor: 'rgba(37, 99, 235, 0.1)',
                    borderLeft: '2px solid #2563eb'
                  }
                }}
              />
              
              {/* Resize Handle - Bottom Edge */}
              <Box
                onMouseDown={(e) => {
                  e.preventDefault();
                  const currentSize = getChatSize(chat._id);
                  setResizing({
                    chatId: chat._id,
                    startX: e.clientX,
                    startY: e.clientY,
                    startWidth: currentSize.width,
                    startHeight: currentSize.height,
                    resizeType: 'height'
                  });
                }}
                sx={{
                  position: 'absolute',
                  left: 8,
                  right: 8,
                  bottom: 0,
                  height: '8px',
                  cursor: 'ns-resize',
                  backgroundColor: 'transparent',
                  zIndex: 10,
                  '&:hover': {
                    backgroundColor: 'rgba(37, 99, 235, 0.1)',
                    borderBottom: '2px solid #2563eb'
                  }
                }}
              />
              
              {/* Resize Handle - Corner (Top-Left) */}
              <Box
                onMouseDown={(e) => {
                  e.preventDefault();
                  const currentSize = getChatSize(chat._id);
                  setResizing({
                    chatId: chat._id,
                    startX: e.clientX,
                    startY: e.clientY,
                    startWidth: currentSize.width,
                    startHeight: currentSize.height,
                    resizeType: 'bothTop'
                  });
                }}
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '16px',
                  height: '16px',
                  cursor: 'nw-resize',
                  backgroundColor: 'transparent',
                  zIndex: 11,
                  '&:hover': {
                    backgroundColor: 'rgba(37, 99, 235, 0.2)'
                  },
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    left: '2px',
                    top: '2px',
                    width: '0',
                    height: '0',
                    borderStyle: 'solid',
                    borderWidth: '12px 12px 0 0',
                    borderColor: 'rgba(156, 163, 175, 0.5) transparent transparent transparent'
                  }
                }}
              />
              <MessageConversation
                conversation={chat}
                open={!chat.isMinimized}
                onClose={() => toggleChatMinimize(chat._id)}
                onMessageSent={() => {
                  loadConversations();
                  // Clear unread and flashing for this chat
                  setUnreadByChat(prev => {
                    const next = { ...prev };
                    delete next[chat._id];
                    return next;
                  });
                  setFlashingChats(prev => {
                    const next = new Set(prev);
                    next.delete(chat._id);
                    return next;
                  });
                }}
                embedded={true}
              />
            </Paper>
          </Slide>
        );
      })}

      {/* New Message Dialog */}
      <Dialog
        open={newMessageOpen}
        onClose={() => setNewMessageOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          New Message
          <IconButton
            onClick={() => setNewMessageOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search for users or teams..."
            value={userSearchQuery}
            onChange={(e) => handleUserSearch(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          
          {/* Selected Recipients */}
          {(selectedUsers.length > 0 || selectedTeam) && (
            <Box sx={{ mb: 2 }}>
              {selectedTeam && (
                <Chip
                  key={selectedTeam._id}
                  color="primary"
                  label={`Team: ${selectedTeam.name || selectedTeam.displayName || 'Unnamed Team'}`}
                  onDelete={() => setSelectedTeam(null)}
                  sx={{ mr: 1, mb: 1 }}
                />
              )}
              {selectedUsers.map(user => (
                <Chip
                  key={user._id}
                  label={user.name}
                  onDelete={() => toggleUserSelection(user)}
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          )}
          
          {/* Search Results */}
          {searchingUsers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List>
              {searchedUsers.length > 0 && (
                <Typography variant="overline" sx={{ pl: 2, color: 'text.secondary' }}>Users</Typography>
              )}
              {searchedUsers.map(user => (
                <ListItem
                  key={user._id}
                  button
                  onClick={() => toggleUserSelection(user)}
                  selected={selectedUsers.some(u => u._id === user._id)}
                >
                  <ListItemAvatar>
                    <Avatar src={user.avatar ? `/api/auth/avatar-proxy?url=${encodeURIComponent(user.avatar)}` : undefined}>
                      {((user?.name || '').charAt(0).toUpperCase() || '?')}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={user.name}
                    secondary={user.email}
                  />
                </ListItem>
              ))}

              {searchedTeams.length > 0 && (
                <>
                  {searchedUsers.length > 0 && <Divider sx={{ my: 1 }} />}
                  <Typography variant="overline" sx={{ pl: 2, color: 'text.secondary' }}>Teams</Typography>
                  {searchedTeams.map(team => (
                    <ListItem
                      key={team._id}
                      button
                      onClick={() => toggleTeamSelection(team)}
                      selected={!!(selectedTeam && selectedTeam._id === team._id)}
                    >
                      <ListItemAvatar>
                        <Avatar src={team.avatar ? `/api/teams/${team._id}/avatar` : undefined}>
                          <GroupIcon fontSize="small" />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={team.name || team.displayName || 'Unnamed Team'}
                        secondary={team.email || team.description || null}
                      />
                    </ListItem>
                  ))}
                </>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewMessageOpen(false)}>Cancel</Button>
          <Button
            onClick={startNewConversation}
            variant="contained"
            color="primary"
            sx={{
              color: '#FFFFFF',
              '&.Mui-disabled': {
                backgroundColor: '#9e9e9e',
                color: '#FFFFFF'
              }
            }}
            disabled={!selectedTeam && selectedUsers.length === 0}
          >
            Start Conversation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Conversation Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeConversationMenu}
        sx={{ zIndex: 2100 }}
        PaperProps={{
          sx: { minWidth: 150 }
        }}
      >
        <MenuItem onClick={() => handleDeleteConversation(menuConversation)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          Delete Conversation
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
        sx={{ zIndex: 2200 }}
        slotProps={{
          backdrop: { sx: { zIndex: 2199 } }
        }}
      >
        <DialogTitle>Delete Conversation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this conversation with{' '}            
            <strong>
              {conversationToDelete ? 
                messageService.getConversationDisplayName(conversationToDelete, user._id) 
                : ''
              }
            </strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. The conversation will be removed from your chat list.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button 
            onClick={confirmDeleteConversation}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MessageSidebar;